import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { listMyTwins, createTwin, setActiveTwin, deleteTwin } from "@/lib/twins.functions";

type Twin = {
  id: string;
  name: string;
  logo_url: string | null;
  is_public: boolean;
  plan: string;
  twin_status: string;
  role: "owner" | "admin" | "member";
  active: boolean;
};

// Workspace-style switcher: the active twin drives every dashboard query
// server-side (resolveCompany), so switching does a full reload to refetch
// everything cleanly against the newly active twin.
export function TwinSwitcher() {
  const { t } = useTranslation();
  const fetchTwins = useServerFn(listMyTwins);
  const create = useServerFn(createTwin);
  const activate = useServerFn(setActiveTwin);
  const removeTwin = useServerFn(deleteTwin);
  const ref = useRef<HTMLDivElement>(null);

  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(!!s));
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const { data } = useQuery({
    queryKey: ["twin-switcher"],
    queryFn: () => fetchTwins(),
    enabled: hasSession,
    staleTime: 30_000,
    retry: false,
  });

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (!data) {
    return (
      <div className="rounded-xl border border-border bg-card px-3 py-2.5 flex items-center gap-2.5">
        <div className="h-7 w-7 rounded-lg bg-cream animate-pulse" />
        <div className="h-3.5 w-24 rounded bg-cream animate-pulse" />
      </div>
    );
  }

  const owned = data.twins as Twin[];
  const memberships = (data.memberships ?? []) as Twin[];
  const active = owned.find((x) => x.active) ?? owned[0] ?? memberships[0] ?? null;
  // Only one twin and nothing to add → no switcher chrome, keep the UI calm.
  const trivial = owned.length + memberships.length <= 1 && !data.canCreate;

  async function switchTo(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      await activate({ data: { companyId: id } });
      // Full reload so every server-resolved query targets the new twin.
      window.location.reload();
    } catch (e: any) {
      toast.error(
        e?.message ??
          t("app.shell.twinSwitcher.couldNotSwitch", { defaultValue: "Could not switch twin" }),
      );
      setBusy(false);
    }
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      const r = await create({ data: { name: newName.trim() } });
      await activate({ data: { companyId: r.id } });
      toast.success(t("app.shell.twinSwitcher.created", { defaultValue: "Twin created" }));
      window.location.reload();
    } catch (e: any) {
      toast.error(
        e?.message ??
          t("app.shell.twinSwitcher.couldNotCreate", { defaultValue: "Could not create twin" }),
      );
      setBusy(false);
    }
  }

  async function handleDelete(tw: Twin) {
    if (busy) return;
    const msg = t("app.shell.twinSwitcher.confirmDelete", {
      defaultValue: `Permanently delete "${tw.name}" and all its data? This cannot be undone.`,
      name: tw.name,
    });
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await removeTwin({ data: { companyId: tw.id } });
      toast.success(t("app.shell.twinSwitcher.deleted", { defaultValue: "Twin deleted" }));
      window.location.reload();
    } catch (e: any) {
      toast.error(
        e?.message ??
          t("app.shell.twinSwitcher.couldNotDelete", { defaultValue: "Could not delete twin" }),
      );
      setBusy(false);
    }
  }

  const initial = (active?.name?.[0] ?? "?").toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={trivial}
        className={`w-full rounded-xl border border-border bg-card px-3 py-2.5 flex items-center gap-2.5 text-left transition ${
          trivial ? "" : "hover:bg-cream"
        }`}
      >
        <span className="h-7 w-7 shrink-0 rounded-lg bg-foreground text-background overflow-hidden flex items-center justify-center text-[13px] font-semibold">
          {active?.logo_url ? (
            <img src={active.logo_url} alt="" className="h-full w-full object-cover" />
          ) : (
            initial
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium truncate">{active?.name ?? "—"}</span>
          <span className="block text-[11px] text-muted-foreground truncate">
            {t("app.shell.twinSwitcher.label", { defaultValue: "Active twin" })}
          </span>
        </span>
        {!trivial && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>

      {open && !trivial && (
        <div className="absolute z-50 left-0 right-0 mt-2 rounded-xl border border-border bg-background shadow-window overflow-hidden">
          <div className="max-h-[280px] overflow-y-auto py-1">
            {owned.map((tw) => (
              <div
                key={tw.id}
                className="group flex items-center gap-2.5 px-3 py-2 hover:bg-cream transition"
              >
                <button
                  onClick={() => (tw.active ? setOpen(false) : switchTo(tw.id))}
                  className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
                >
                  <span className="h-6 w-6 shrink-0 rounded-md bg-foreground text-background overflow-hidden flex items-center justify-center text-[11px] font-semibold">
                    {tw.logo_url ? (
                      <img src={tw.logo_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      (tw.name[0] ?? "?").toUpperCase()
                    )}
                  </span>
                  <span className="min-w-0 flex-1 text-[13px] truncate">{tw.name}</span>
                  {tw.active && <Check className="h-3.5 w-3.5 shrink-0 text-foreground" />}
                </button>
                {owned.length > 1 && (
                  <button
                    onClick={() => handleDelete(tw)}
                    title={t("app.shell.twinSwitcher.delete", { defaultValue: "Delete twin" })}
                    className="shrink-0 p-1 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-500 transition"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}

            {memberships.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                  {t("app.shell.twinSwitcher.shared", { defaultValue: "Shared with me" })}
                </div>
                {memberships.map((tw) => (
                  <button
                    key={tw.id}
                    onClick={() => switchTo(tw.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-cream transition"
                  >
                    <span className="h-6 w-6 shrink-0 rounded-md bg-cream border border-border overflow-hidden flex items-center justify-center text-[11px] font-semibold">
                      {tw.logo_url ? (
                        <img src={tw.logo_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        (tw.name[0] ?? "?").toUpperCase()
                      )}
                    </span>
                    <span className="min-w-0 flex-1 text-[13px] truncate">{tw.name}</span>
                    <span className="text-[10px] text-muted-foreground capitalize">{tw.role}</span>
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="border-t border-border p-1.5">
            {creating ? (
              <form onSubmit={submitCreate} className="flex items-center gap-1.5 p-1">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={120}
                  placeholder={t("app.shell.twinSwitcher.namePlaceholder", {
                    defaultValue: "New twin name",
                  })}
                  className="flex-1 min-w-0 rounded-lg bg-cream border border-border px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-foreground/40"
                />
                <button
                  type="submit"
                  disabled={busy || !newName.trim()}
                  className="shrink-0 rounded-lg bg-foreground text-background px-2.5 py-1.5 text-[12px] font-medium disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t("app.shell.twinSwitcher.create", { defaultValue: "Create" })
                  )}
                </button>
              </form>
            ) : data.canCreate ? (
              <button
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-foreground hover:bg-cream rounded-lg transition"
              >
                <Plus className="h-4 w-4" />
                {t("app.shell.twinSwitcher.newTwin", { defaultValue: "New twin" })}
              </button>
            ) : (
              <Link
                to="/billing"
                onClick={() => setOpen(false)}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-muted-foreground hover:bg-cream rounded-lg transition"
                title={t("app.shell.twinSwitcher.limitReached", {
                  defaultValue: "Twin limit reached — upgrade for more",
                })}
              >
                <Lock className="h-3.5 w-3.5" />
                {t("app.shell.twinSwitcher.upgradeForMore", {
                  defaultValue: "Upgrade for more twins",
                })}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
